import jsPDF from 'jspdf';
import { TaskImageBase64 } from '../../../../../services/openapi/models/TaskImageBase64';

/**
 * Image Processing and Manipulation Utilities
 */

export interface ImageDimensions {
    width: number;
    height: number;
}

export interface ImageData {
    dataUrl: string;
    format: string;
    name?: string;
}

/**
 * Get backend URL based on environment
 */
export const getBackendUrl = (): string => {
    return import.meta.env.DEV
        ? 'http://localhost:8008'
        : import.meta.env.VITE_BACKEND_URL || '';
};

/**
 * Detect image format from file type or URL
 */
export const detectImageFormat = (fileOrUrl: File | string): string => {
    const source = typeof fileOrUrl === 'string' ? fileOrUrl.toLowerCase() : fileOrUrl.type.toLowerCase();

    if (source.includes('png')) return 'PNG';
    if (source.includes('gif')) return 'GIF';
    if (source.includes('webp')) return 'WEBP';
    return 'JPEG';
};

/**
 * Compress an image using canvas
 * Reduces resolution and applies JPEG compression
 */
export const compressImage = (
    dataUrl: string,
    maxDimension: number = 1200,
    quality: number = 0.8
): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
            try {
                let { width, height } = img;

                // Scale down if larger than maxDimension
                if (width > maxDimension || height > maxDimension) {
                    if (width > height) {
                        height = Math.round((height * maxDimension) / width);
                        width = maxDimension;
                    } else {
                        width = Math.round((width * maxDimension) / height);
                        height = maxDimension;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Could not get canvas context'));
                    return;
                }

                // Use white background for JPEG (no transparency)
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, width, height);

                // Draw image with smoothing for better quality when scaling
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0, width, height);

                // Always output as JPEG for better compression
                const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
                resolve(compressedDataUrl);

            } catch (error) {
                reject(error);
            }
        };

        img.onerror = () => {
            reject(new Error('Failed to load image for compression'));
        };

        img.src = dataUrl;
    });
};

/**
 * Calculate image dimensions to fit within max bounds while maintaining aspect ratio
 */
export const calculateImageDimensions = (
    img: HTMLImageElement,
    maxWidth: number,
    maxHeight: number
): ImageDimensions => {
    if (img.width === 0 || img.height === 0) {
        throw new Error('Invalid image dimensions');
    }

    const aspectRatio = img.width / img.height;
    let width = maxWidth;
    let height = maxHeight;

    if (aspectRatio > 1) {
        // Landscape orientation
        height = width / aspectRatio;
        if (height > maxHeight) {
            height = maxHeight;
            width = height * aspectRatio;
        }
    } else {
        // Portrait orientation
        width = height * aspectRatio;
        if (width > maxWidth) {
            width = maxWidth;
            height = width / aspectRatio;
        }
    }

    return { width, height };
};

/**
 * Convert files to base64 data URLs
 */
export const convertFilesToBase64 = async (files: File[]): Promise<ImageData[]> => {
    const results: ImageData[] = [];

    for (const file of files) {
        try {
            const dataUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });

            results.push({
                dataUrl,
                format: detectImageFormat(file),
                name: file.name
            });

        } catch (error) {
            console.warn('Failed to convert file to base64:', file.name, error);
        }
    }

    return results;
};

/**
 * Load image from URL and convert to data URL
 */
export const loadImageFromUrl = (url: string): Promise<ImageData> => {
    return new Promise((resolve, reject) => {
        const format = detectImageFormat(url);

        let imageUrl = url;
        if (url.startsWith('/')) {
            const backendUrl = getBackendUrl();
            imageUrl = `${backendUrl}${url}`;
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Could not get canvas context'));
                    return;
                }

                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);

                const outputFormat = format === 'PNG' ? 'image/png' : 'image/jpeg';
                const dataUrl = canvas.toDataURL(outputFormat, 0.9);

                resolve({
                    dataUrl,
                    format: format === 'PNG' ? 'PNG' : 'JPEG'
                });

            } catch (canvasError) {
                // Fallback: try fetch with blob
                fetch(imageUrl, { credentials: 'include' })
                    .then(response => response.blob())
                    .then(blob => {
                        const reader = new FileReader();
                        reader.onload = () => {
                            resolve({
                                dataUrl: reader.result as string,
                                format: format === 'PNG' ? 'PNG' : 'JPEG'
                            });
                        };
                        reader.onerror = () => reject(new Error('FileReader failed'));
                        reader.readAsDataURL(blob);
                    })
                    .catch(() => {
                        reject(new Error(`All loading methods failed for: ${imageUrl}`));
                    });
            }
        };

        img.onerror = () => {
            reject(new Error(`Failed to load image: ${imageUrl}`));
        };

        img.src = imageUrl;
    });
};

/**
 * Add image to PDF with automatic dimension calculation
 */
export const addImageToPDF = async (
    doc: jsPDF,
    imageData: ImageData | TaskImageBase64,
    x: number,
    y: number,
    maxWidth: number = 160,
    maxHeight: number = 100
): Promise<number> => {
    try {
        // Extract data URL and format
        const originalDataUrl = 'data_url' in imageData ? imageData.data_url : imageData.dataUrl;
        const name = 'name' in imageData ? imageData.name :
                     'image_path' in imageData ? imageData.image_path.split('/').pop() : 'unknown';

        if (!originalDataUrl.startsWith('data:')) {
            throw new Error('Invalid data URL format');
        }

        // Compress the image before adding to PDF
        let compressedDataUrl: string;
        try {
            compressedDataUrl = await compressImage(originalDataUrl, 1200, 0.7);
        } catch (compressionError) {
            console.warn('Image compression failed, using original:', compressionError);
            compressedDataUrl = originalDataUrl;
        }

        return new Promise((resolve) => {
            const img = new Image();

            img.onload = () => {
                try {
                    const dimensions = calculateImageDimensions(img, maxWidth, maxHeight);

                    // Always use JPEG format for compressed images
                    doc.addImage(compressedDataUrl, 'JPEG', x, y, dimensions.width, dimensions.height);
                    resolve(y + dimensions.height + 10);

                } catch (error) {
                    console.error('Error adding image to PDF:', error);
                    doc.setFontSize(10);
                    doc.setTextColor(255, 0, 0);
                    doc.text(`[Fehler beim Hinzufügen des Bildes]`, x, y + 10);
                    resolve(y + 20);
                }
            };

            img.onerror = () => {
                console.error('Error loading image for PDF');
                doc.setFontSize(10);
                doc.setTextColor(255, 0, 0);
                doc.text(`[Bild konnte nicht geladen werden${name ? `: ${name}` : ''}]`, x, y + 10);
                resolve(y + 20);
            };

            img.src = compressedDataUrl;
        });

    } catch (error) {
        console.error('Could not process image for PDF:', error);

        doc.setFontSize(10);
        doc.setTextColor(255, 0, 0);
        doc.text(`[Bild konnte nicht geladen werden]`, x, y + 10);

        return y + 35;
    }
};

/**
 * Add image from URL to PDF (with URL loading fallback)
 */
export const addImageFromUrlToPDF = async (
    doc: jsPDF,
    imageUrl: string,
    x: number,
    y: number,
    maxWidth: number = 160,
    maxHeight: number = 100
): Promise<number> => {
    try {
        const imageData = await loadImageFromUrl(imageUrl);
        return addImageToPDF(doc, imageData, x, y, maxWidth, maxHeight);
    } catch (error) {
        console.error('Could not load image from URL for PDF:', imageUrl, error);
        doc.setFontSize(10);
        doc.setTextColor(255, 0, 0);
        doc.text(`[Bild konnte nicht geladen werden]`, x, y + 10);
        return y + 35;
    }
};
